import { useState, useCallback } from 'react';
import { formatReference, type BibleLocation } from '@skjv/common';
import { CloseIcon } from '@components/icons';

interface LocationDropTargetProps {
  label: string;
  location?: BibleLocation;
  onDrop: (location: BibleLocation | null) => void;
  placeholder?: string;
  required?: boolean;
  large?: boolean;
  /** Anchor for guided-tour highlights (rendered as data-guide) */
  dataGuide?: string;
}

/**
 * Drop target for verse locations. Users drag verse labels from Bible text.
 */
export function LocationDropTarget({
  label,
  location,
  onDrop,
  placeholder = 'Drop verse here',
  required = false,
  large = false,
  dataGuide,
}: LocationDropTargetProps) {
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);

      try {
        const data = e.dataTransfer.getData('application/json');
        if (data) {
          const locationData = JSON.parse(data) as BibleLocation;
          if (
            typeof locationData.testamentIndex === 'number' &&
            typeof locationData.bookIndex === 'number' &&
            typeof locationData.chapterIndex === 'number' &&
            typeof locationData.verseIndex === 'number'
          ) {
            onDrop(locationData);
          }
        }
      } catch {
        // Invalid drop data
      }
    },
    [onDrop],
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onDrop(null);
    },
    [onDrop],
  );

  const baseClasses = large ? 'p-6 rounded-xl min-h-[120px]' : 'p-3 rounded-lg min-h-[60px]';

  const borderClasses = isDragOver
    ? 'border-2 border-dashed border-cyan-500 bg-cyan-50/50 dark:bg-cyan-900/20'
    : location
      ? 'border border-solid border-cyan-400/50 dark:border-cyan-600/50 bg-cyan-50/30 dark:bg-cyan-900/10'
      : 'border-2 border-dashed border-stone-300 dark:border-stone-600 bg-stone-100/30 dark:bg-stone-800/30';

  return (
    <div
      data-guide={dataGuide}
      className={`${baseClasses} ${borderClasses} flex cursor-pointer flex-col items-center justify-center text-center transition-all`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <span
        className={`mb-1 text-xs font-medium ${
          required && !location
            ? 'text-amber-600 dark:text-amber-400'
            : 'text-stone-500 dark:text-stone-400'
        }`}
      >
        {label}
        {required && !location && ' *'}
      </span>

      {location ? (
        <div className="flex items-center gap-1">
          <span className="text-sm font-medium text-cyan-700 dark:text-cyan-300">
            {formatReference(location)}
          </span>
          <button
            onClick={handleClear}
            className="p-0.5 text-stone-400 transition-colors hover:text-red-500"
            title="Clear"
          >
            <CloseIcon className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <span
          className={`text-sm ${
            isDragOver ? 'text-cyan-600 dark:text-cyan-400' : 'text-stone-400 dark:text-stone-500'
          }`}
        >
          {isDragOver ? 'Drop here!' : placeholder}
        </span>
      )}
    </div>
  );
}
